import { useState } from "react";
import { motion } from "framer-motion";
import { Icon, ICON_BY_NUMBER } from "./ServiceIcons";

const EASE = [0.16, 1, 0.3, 1];

export const ServiceCard = ({ service, variant = "light" }) => {
    const [hover, setHover] = useState(false);
    const isDark = variant === "dark";

    return (
        <motion.article
            data-testid={`service-card-${service.n}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className={`group relative border ${
                isDark
                    ? "border-white/15 bg-[#111111] text-white hover:bg-white hover:text-[#111111]"
                    : "border-[#dddddd] bg-white text-[#111111] hover:bg-[#111111] hover:text-white"
            } p-8 md:p-10 h-full flex flex-col transition-colors duration-700 overflow-hidden`}
        >
            {/* Floating index ring */}
            <div className="flex items-start justify-between">
                <span className="text-[10px] tracking-[0.28em] uppercase font-medium opacity-70">
                    {service.n} · {service.kicker}
                </span>
                <span
                    aria-hidden
                    className="text-2xl transition-transform duration-700 group-hover:rotate-45"
                >
                    +
                </span>
            </div>

            {/* Icon block with hover transform */}
            <div className="my-10 relative h-20 md:h-24">
                <motion.div
                    animate={{ y: hover ? -6 : 0, rotate: hover ? -2 : 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="inline-block"
                >
                    <Icon
                        name={ICON_BY_NUMBER[service.n] || "site"}
                        className="w-16 h-16 md:w-20 md:h-20"
                    />
                </motion.div>
                {/* Concentric backdrop dots */}
                <div className="absolute right-0 top-0 opacity-30">
                    <svg viewBox="0 0 60 60" className="w-12 h-12">
                        <circle cx="30" cy="30" r="28" fill="none" stroke="currentColor" strokeWidth="0.6" strokeDasharray="2 4" />
                        <circle cx="30" cy="30" r="18" fill="none" stroke="currentColor" strokeWidth="0.6" />
                        <circle cx="30" cy="30" r="2" fill="currentColor" />
                    </svg>
                </div>
            </div>

            <h3 className="font-serif text-2xl md:text-3xl leading-tight">
                {service.title}
            </h3>

            <p className={`mt-4 text-[15px] leading-relaxed flex-1 ${
                isDark ? "text-white/70 group-hover:text-[#444444]" : "text-[#444444] group-hover:text-white/70"
            } transition-colors duration-700`}>
                {service.body}
            </p>

            {/* Outcome chip */}
            <div className={`mt-8 pt-6 border-t ${
                isDark ? "border-white/15 group-hover:border-[#dddddd]" : "border-[#dddddd] group-hover:border-white/15"
            } flex items-center justify-between transition-colors duration-700`}>
                <span className="text-[10px] tracking-[0.28em] uppercase">
                    Outcome
                </span>
                <span className="font-serif italic text-base">
                    Trust → Enquiries
                </span>
            </div>

            {/* Reveal underline */}
            <span
                aria-hidden
                className={`pointer-events-none absolute left-0 bottom-0 h-[2px] w-full ${
                    isDark ? "bg-[#111111]" : "bg-white"
                } origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700`}
                style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
            />
        </motion.article>
    );
};
